<template>
    <!-- DESKTOP -->
    <div
        :excluded-number="excludedOptions.length"
        v-if="!isMobile"
        :class="{
            'viewer-bottom-toolbar': true,
            'is-second': isSecond,
            'is-comparison-active': imageComparisonEnabled,
            'is-tour': isTour,
            'construcode-document-active': isConstrucodeDocumentActive,
        }"
    >
        <button v-if="nextAndPrevArrowsEnabled" v-b-tooltip.hover :title="previousViewpoint.name" class="prev-viewpoint-button" @click="requestPrevViewpoint">
            <icon name="angle-double-left" scale="1" />
        </button>

        <div class="buttons-container">
            <!-- Directionals -->
            <button
                v-if="!isOptionExcluded('left')"
                v-b-tooltip.hover
                :title="$t('Components_Viewer_UI_BottomToolbar_1')"
                @mousedown="start('lookAtLeft')"
                @mouseup="stop()"
                @mouseleave="stop()"
            >
                <icon name="angle-left" scale="1" />
            </button>
            <button
                v-if="!isOptionExcluded('right')"
                v-b-tooltip.hover
                :title="$t('Components_Viewer_UI_BottomToolbar_2')"
                @mousedown="start('lookAtRight')"
                @mouseup="stop()"
                @mouseleave="stop()"
            >
                <icon name="angle-right" scale="1" />
            </button>
            <button
                v-if="!isOptionExcluded('up')"
                v-b-tooltip.hover
                :title="$t('Components_Viewer_UI_BottomToolbar_3')"
                @mousedown="start('lookAtUp')"
                @mouseup="stop()"
                @mouseleave="stop()"
            >
                <icon name="angle-up" scale="1" />
            </button>
            <button
                v-if="!isOptionExcluded('down')"
                v-b-tooltip.hover
                :title="$t('Components_Viewer_UI_BottomToolbar_4')"
                @mousedown="start('lookAtDown')"
                @mouseup="stop()"
                @mouseleave="stop()"
            >
                <icon name="angle-down" scale="1" />
            </button>
            <button v-if="!isOptionExcluded('turn-around')" v-b-tooltip.hover :title="$t('Components_Viewer_UI_BottomToolbar_5')" @click="lookBack">
                <icon name="turn-around" scale="0.8" />
            </button>

            <!-- Zoom -->
            <button
                v-if="!isOptionExcluded('zoom-in')"
                v-b-tooltip.hover
                :title="$t('Components_Viewer_UI_BottomToolbar_6')"
                @mousedown="start('zoomIn')"
                @mouseup="stop()"
                @mouseleave="stop()"
            >
                <icon name="search-plus" scale="0.8" />
            </button>
            <button
                v-if="!isOptionExcluded('zoom-out')"
                v-b-tooltip.hover
                :title="$t('Components_Viewer_UI_BottomToolbar_7')"
                @mousedown="start('zoomOut')"
                @mouseup="stop()"
                @mouseleave="stop()"
            >
                <icon name="search-minus" scale="0.8" />
            </button>

            <!-- Fullscreen -->
            <button
                v-b-tooltip.hover
                :title="$t('Components_Viewer_UI_BottomToolbar_8')"
                @click="requestFullscreen"
                v-if="!isFullscreenEnabled && !isOptionExcluded('fullscreen')"
            >
                <icon name="expand" scale="0.8" />
            </button>
            <button v-b-tooltip.hover :title="$t('Components_Viewer_UI_BottomToolbar_9')" @click="exitFullscreen" v-else-if="!isOptionExcluded('fullscreen')">
                <icon name="compress" scale="0.8" />
            </button>
        </div>

        <button v-if="nextAndPrevArrowsEnabled" v-b-tooltip.hover :title="nextViewpoint.name" class="next-viewpoint-button" @click="requestNextViewpoint">
            <icon name="angle-double-right" scale="1" />
        </button>
    </div>

    <!-- MOBILE -->
    <div v-else id="mobile-viewer-controls" class="d-flex justify-content-center align-items-center">
        <div class="viewer-controls-container">
            <div class="prev-pic-ctrl" @click="requestPrevViewpoint">
                <icon name="angle-left" color="white" />
            </div>
            <div class="current-viewpoint-name d-flex justify-content-center align-items-center">
                {{ currentViewpoint.name }}
            </div>
            <div class="next-pic-ctrl" @click="requestNextViewpoint">
                <icon name="angle-right" color="white" />
            </div>
        </div>
    </div>
</template>

<script>
import 'vue-awesome/icons/angle-left'
import 'vue-awesome/icons/angle-double-left'
import 'vue-awesome/icons/angle-right'
import 'vue-awesome/icons/angle-double-right'
import 'vue-awesome/icons/angle-down'
import 'vue-awesome/icons/angle-up'
import 'vue-awesome/icons/search-plus'
import 'vue-awesome/icons/search-minus'
import 'vue-awesome/icons/expand'
import 'vue-awesome/icons/compress'

/**
 * Summary.
 * This UI component is used to show the bottom left/right tollbar inside the viewer.
 *
 * @param {Boolean} isMobile
 * @param {Boolean} isFullscreenEnabled
 * @param {Boolean} isSecond - if the component is linked to the right panorama
 * @param {Boolean} imageComparisonEnabled
 * @param {Boolean} nextAndPrevArrowsEnabled
 * @param {Boolean} isTour
 *
 * @param {Object} currentViepwoint
 * @param {Object} nextViewpoint
 * @param {Object} previousViewpoint
 *
 * @param {Array} excludedOptions - exclude the options passed from the bar
 *      - left
 *      - right
 *      - up
 *      - down
 *      - turn-around
 *      - zoom-in
 *      - zoom-out
 *      - fullscreen
 *
 * @event controlClickStart(controlName)
 * @event controlClickStop
 * @event lookBackClick
 * @event onPrevViewpointRequest
 * @event onNextViewpointRequest
 * @event onRequestFullscreen
 * @event onRequestExitFullscreen
 */

export default {
    props: {
        isMobile: Boolean,
        isFullscreenEnabled: Boolean,
        isSecond: Boolean,
        imageComparisonEnabled: Boolean,
        nextAndPrevArrowsEnabled: {
            type: Boolean,
            default: true,
        },
        isTour: Boolean,
        isConstrucodeDocumentActive: Boolean,

        currentViewpoint: {
            type: Object,
            default: () => ({}),
        },
        nextViewpoint: Object,
        previousViewpoint: Object,

        excludedOptions: {
            type: Array,
            default: () => [],
        },
    },

    methods: {
        start(controlName) {
            this.$emit('controlClickStart', controlName)
        },
        stop() {
            this.$emit('controlClickStop')
        },
        lookBack() {
            this.$emit('lookBackClick')
        },
        requestPrevViewpoint() {
            this.$emit('onPrevViewpointRequest')
        },
        requestNextViewpoint() {
            this.$emit('onNextViewpointRequest')
        },
        requestFullscreen() {
            this.$emit('onRequestFullscreen')
        },
        exitFullscreen() {
            this.$emit('onRequestExitFullscreen')
        },

        isOptionExcluded(option) {
            if (this.excludedOptions) {
                return this.excludedOptions.includes(option)
            }

            return false
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/UI/bottom-toolbar.scss';
</style>
